import { ComponentType, createElement, lazy } from 'react';
import { matchPath } from 'react-router-dom';

type PageModule = { default: ComponentType };

/**
 * Como lazy(), mas com preload(): depois do preload o componente renderiza direto,
 * sem suspender. É o que permite hidratar o HTML pré-renderizado sem o React
 * descartar a página pronta e piscar o fallback do Suspense.
 */
function lazyRoute(load: () => Promise<PageModule>) {
  let loaded: ComponentType | null = null;
  const Fallback = lazy(load);
  const Page = () => createElement(loaded ?? Fallback);
  Page.preload = () => load().then((mod) => { loaded = mod.default; });
  return Page;
}

/**
 * Rotas públicas e indexáveis. São as pré-renderizadas no build — a lista de URLs
 * vem de public/sitemap.xml, e os dois precisam continuar combinando.
 */
export const publicRoutes = [
  { path: '/', Page: lazyRoute(() => import('./Index')) },
  { path: '/about', Page: lazyRoute(() => import('./AboutPage')) },
  { path: '/experience', Page: lazyRoute(() => import('./ExperiencePage')) },
  { path: '/projects', Page: lazyRoute(() => import('./ProjectsPage')) },
  { path: '/contact', Page: lazyRoute(() => import('./ContactPage')) },
  { path: '/blog', Page: lazyRoute(() => import('./BlogPage')) },
  { path: '/blog/:slug', Page: lazyRoute(() => import('./BlogPostPage')) },
  { path: '/media', Page: lazyRoute(() => import('./MediaPage')) },
  { path: '/palestras', Page: lazyRoute(() => import('./SpeakingPage')) },
  { path: '/imprensa', Page: lazyRoute(() => import('./PressPage')) },
  { path: '/mentoria', Page: lazyRoute(() => import('./MentoriaPage')) },
  { path: '/bni', Page: lazyRoute(() => import('./BniPage')) },
  { path: '/politica-privacidade', Page: lazyRoute(() => import('./PoliticaPrivacidade')) },
];

/** Carrega o módulo da rota atual antes de a aplicação montar. */
export function preloadRoute(pathname: string): Promise<unknown> {
  const route = publicRoutes.find(({ path }) => matchPath(path, pathname));
  return route ? route.Page.preload() : Promise.resolve();
}
